/**
 * Assistant.jsx
 * The question box.
 *
 * It answers; it does not act. Nothing it says creates, schedules or publishes
 * anything, and the suggestions below are questions rather than commands for
 * that reason — a chat that quietly takes actions is one nobody can audit.
 *
 * The conversation lives here, in the screen, and is sent back with each
 * question. Nothing is stored on the server, so a chat never becomes an
 * undeclared second home for brand information.
 *
 * Dependencies: services/api.js, components/Icons.jsx
 */

import { useEffect, useRef, useState } from 'react';
import { Send, Sparkle, Warning } from '../Icons.jsx';
import { api } from '../../services/api.js';

/** Questions, not commands. The buttons on this panel cannot make anything. */
const SUGGESTIONS = [
  'What should the first week focus on?',
  'Which of these ideas is weakest, and why?',
  'How would you adjust this for a colder audience?',
  'Suggest three hooks for the education posts',
];

export default function Assistant({ brandId, brandName, month, hasStrategy }) {
  const [turns, setTurns] = useState([]);
  const [question, setQuestion] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const endRef = useRef(null);

  // A brand or month change is a different conversation, not a continuation.
  useEffect(() => {
    setTurns([]);
    setError(null);
  }, [brandId, month]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [turns, busy]);

  async function ask(text) {
    const asked = String(text ?? '').trim();
    if (!asked || busy) return;

    setQuestion('');
    setError(null);
    setBusy(true);

    // Shown immediately: waiting with your own words on screen is a very
    // different wait from waiting at an empty box.
    const history = turns.map((t) => ({ role: t.role, content: t.content }));
    setTurns((t) => [...t, { role: 'user', content: asked }]);

    try {
      const res = await api.copilot.ask({ brandId, month, question: asked, history });
      setTurns((t) => [...t, { role: 'assistant', content: res.answer }]);
    } catch (e) {
      setError(e);
      // The question stays in the box so it does not have to be retyped.
      setTurns((t) => t.slice(0, -1));
      setQuestion(asked);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card assistant">
      <div className="card__head">
        <span className="tile">
          <Sparkle size={21} />
        </span>
        <div className="card__head-text">
          <h2>Copilot assistant</h2>
          <p>Ask about the plan. It answers, it doesn’t act.</p>
        </div>
      </div>

      <div className="assistant__thread">
        <div className="assistant__intro">
          <p>
            <strong>Hello.</strong> I can talk through {brandName}’s plan for {month}: what to lead
            with, where an idea is weak, how to angle it for a different audience.
          </p>
          <p className="assistant__caveat">
            I can’t create, schedule or publish anything. Use <em>Generate strategy</em> for the
            plan, <em>Generate the month</em> to turn it into drafts.
          </p>
        </div>

        {turns.map((turn, i) => (
          <div className={`bubble bubble--${turn.role}`} key={i}>
            {turn.content}
          </div>
        ))}

        {busy && (
          <div className="bubble bubble--assistant bubble--thinking">
            <span className="dot-pulse" aria-hidden="true" />
            Thinking…
          </div>
        )}

        {error && (
          <div className="notice notice--error">
            <Warning />
            <span>{error.message}</span>
          </div>
        )}

        <div ref={endRef} />
      </div>

      {turns.length === 0 && (
        <ul className="assistant__suggestions">
          {SUGGESTIONS.map((s) => (
            <li key={s}>
              <button type="button" onClick={() => ask(s)} disabled={busy || !hasStrategy}>
                <Sparkle size={14} /> {s}
              </button>
            </li>
          ))}
        </ul>
      )}

      <form
        className="assistant__form"
        onSubmit={(e) => {
          e.preventDefault();
          ask(question);
        }}
      >
        <input
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder={hasStrategy ? 'Ask Copilot anything…' : 'Generate a strategy first…'}
          maxLength={1000}
          disabled={busy}
          aria-label="Ask Copilot"
        />
        <button
          className="btn btn--primary btn--sm"
          type="submit"
          disabled={busy || !question.trim()}
          aria-label="Send"
        >
          <Send size={15} />
        </button>
      </form>

      {/* Each answer is a real generation against the monthly cap. Saying so
          here is cheaper than a surprise on the Costs screen. */}
      <p className="card__footnote">Each answer counts towards your monthly AI spend.</p>
    </section>
  );
}
